import React, {useContext, useState} from 'react';
import {View, Modal, LayoutChangeEvent, ScrollView, Platform} from 'react-native';
import MainStyle from '../../../../../../MainStyle';
import Button from '../../../../../components/Button';
import {StoreContext} from "../../../../../components/StoreContextProvider";
import {colors} from "../../../../../assets/styles/colors";
import {browser} from "../../../../../utils";
import Div from "../../../../../lib/Div";
import P from "../../../../../lib/P";
import {CancelModalProps} from "../../../../../types";
import {isFullSwing} from "../../../../../constants";

const Internet_connection_issues_message = 'Internet connection issues detected. Please reload the page.';
const Location_is_turned_offSm = `1. If you selected "Don't Allow", reload your website. You'll be prompted to Allow Location Access again.
Select "Allow".

2. If you didn't see a pop-up, follow the instructions on this link:
`;

type Props = { config?: CancelModalProps };

const CancelModal = ({config}: Props) => {
  const Context = useContext(StoreContext);
  const cancelModalConfig = config || Context.cancelModalConfig;
  const {onLine} = Context;
  const {visible, title, subtitle, secondary, leftBtnText, rightBtnText, titleColor, cancelGray} = cancelModalConfig;
  const {leftBtnBackgroundColor, leftBtnFontSize, rightBtnFontSize, rightBtnBackgroundColor} = cancelModalConfig;
  const {rightBtnBorderColor, rightBtnBorderWidth, rightBtnColor} = cancelModalConfig;
  const [containerHeight, setContainerHeight] = useState(164);

  const renderSecondary = () => {
    if (subtitle === 'Permission to access the location was denied.') return 'Note: You can still access your account.';
    else return secondary;
  };

  const renderTitle = () => {
    if (subtitle === 'Permission to access the location was denied.') return 'TURN ON LOCATION ACCESS';
    else return title;
  };

  const renderSubtitle = () => {
    if (subtitle === 'Permission to access the location was denied.') return Location_is_turned_offSm;
    else return subtitle;
  };

  if (Platform.OS === 'web' && visible && !onLine && subtitle !== Internet_connection_issues_message) {
    Context.setCancelModalConfig({
      visible: true, title: 'ERROR', leftBtnText: 'OK', subtitle: Internet_connection_issues_message,
      onExit: () => {
        Context.setCancelModalConfig({
          ...cancelModalConfig, subtitle: Internet_connection_issues_message, visible: false
        });
      }
    });
  }

  const enablingLocationGuide = () => {
    if (Platform.OS === 'web' && subtitle === 'Permission to access the location was denied.') {
      return `https://www.google.com/search?q=how+do+I+turn+on+location+for+a+website+in+${browser()}`;
    }
  };

  const onLayout = (event: LayoutChangeEvent) => {
    const {height} = event.nativeEvent.layout;
    setContainerHeight(height);
  };

  return (
    <Modal transparent visible={!!visible} statusBarTranslucent={false} hardwareAccelerated={false}
           onRequestClose={cancelModalConfig.onResume}>
      <Div h="100%" w="100%" bg={colors.modalBg} style={MainStyle.fix_gradient_and_modal_error_in_landscape}>
        <ScrollView contentContainerStyle={[MainStyle.full_h, MainStyle.full_w, MainStyle.a_i_c, MainStyle.j_c_c]}>
          <Div mx={16} maxW={328} bg={isFullSwing ? '#131314' : colors.bottomTabBg} minH={164} borderWidth={1}
               borderColor={isFullSwing ? undefined : colors.popupBorder} rounded={isFullSwing ? 16 : 4} py={23} px={16}
               position="relative" w="100%" onLayout={onLayout}>
            <P color={colors.newDangerText} fw="700" fs={20} lh={27} ta="center" mb={22} style={titleColor}>
              {renderTitle()}
            </P>

            <P lh={19} ta="center">{renderSubtitle()}</P>

            {enablingLocationGuide() && (
              <P lh={19} ta="center" my={10} color={colors.newLightBlue}
                 onPress={() => window.open(enablingLocationGuide(), '_blank')}>
                How To Enable Location
              </P>
            )}

            {!!renderSecondary() && <P lh={19} ta="center">{renderSecondary()}</P>}

            {(leftBtnText || rightBtnText) ? (
              <Div position="absolute" left={0} right={0} alignItems="center" top={containerHeight + 60}>
                <View>
                  {(!!cancelModalConfig.onExit && leftBtnText) && (
                    <Button
                      h={63} w={244} text={leftBtnText} onPress={cancelModalConfig.onExit} mb={16}
                      radius={isFullSwing ? 4 : undefined} bg={isFullSwing ? '#3369FF' : leftBtnBackgroundColor}
                      fontSize={leftBtnFontSize} color={isFullSwing ? 'white' : undefined}
                    />
                  )}

                  <Div mb={27} h={40}>
                    {(!!cancelModalConfig.onResume && rightBtnText) && (
                      <Button
                        text={rightBtnText} onPress={cancelModalConfig.onResume} w={244} h={63}
                        fontSize={rightBtnFontSize} bg={isFullSwing ? '#3369FF' : rightBtnBackgroundColor}
                        borderColor={rightBtnBorderColor}
                        borderWidth={rightBtnBorderWidth} color={isFullSwing ? 'white' : rightBtnColor}
                        radius={isFullSwing ? 4 : undefined}
                      />
                    )}
                  </Div>
                </View>

                <Div h={22}>
                  {(!!cancelModalConfig.onResume && rightBtnText && !!cancelModalConfig.onClose) && (
                    <Button
                      text="CANCEL" onPress={cancelModalConfig.onClose} w={244} h={63} bg="transparent"
                      borderColor={cancelGray ? 'gray' : colors.popupDangerPrimary} borderWidth={1}
                      textStyle={[cancelGray ? MainStyle.c_gray : MainStyle.c_warn]}
                      radius={isFullSwing ? 4 : undefined}
                    />
                  )}
                </Div>
              </Div>
            ) : (
              <Div position="absolute" left={0} right={0} alignItems="center" top={containerHeight + 110}>
                <Div alignItems="center" row w={244}>
                  {!!cancelModalConfig.onExit && (
                    <Button
                      text="Yes" onPress={cancelModalConfig.onExit} bg={isFullSwing ? '#3369FF' : undefined}
                      radius={isFullSwing ? 4 : undefined} color={isFullSwing ? 'white' : undefined}
                      style={MainStyle.flex1}
                    />
                  )}

                  <Div w={8}/>

                  {!!cancelModalConfig.onResume && (
                    <Button
                      text="No" onPress={cancelModalConfig.onResume} style={MainStyle.flex1}
                      bg={isFullSwing ? '#3369FF' : undefined} radius={isFullSwing ? 4 : undefined}
                      color={isFullSwing ? 'white' : undefined}
                    />
                  )}
                </Div>
              </Div>
            )}
          </Div>
        </ScrollView>
      </Div>
    </Modal>
  );
};

export default React.memo(CancelModal);
